import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Gift, Inbox, LayoutGrid, MoreHorizontal, Package, Paperclip, Send, Tag, X } from "lucide-react";
import toast from "react-hot-toast";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SpinnerBlock } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  getTikTokInboxCounts,
  listTikTokAffiliateProducts,
  listTikTokCollaborations,
  listTikTokConversations,
  listTikTokMessages,
  listTikTokSamples,
  markTikTokConversationRead,
  openTikTokConversation,
  sendTikTokMessage,
  setTikTokConversationStatus,
  type TikTokAffiliateProduct,
  type TikTokConversation,
  type TikTokConversationFilter,
  type TikTokCreatorPick,
  type TikTokMessage,
  type TikTokOutgoingMessage,
  type TikTokSampleApplication,
} from "@/services/tiktokShop.service";

import { TikTokBlockedState } from "../TikTokBlockedState";
import { blockFromError, errorMessage } from "../tiktokFormat";

const FILTERS: Array<{ value: TikTokConversationFilter; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "UNREAD", label: "Unread" },
  { value: "UNREPLIED", label: "Unreplied" },
  { value: "STARRED", label: "Starred" },
  { value: "ARCHIVED", label: "Archived" },
];
const TEXT_MAX = 2000;
const CARD_TEXT_MAX = 500;

type Attach = "product" | "collab" | "sample" | "showcase" | null;

interface Lookups {
  products: Map<string, TikTokAffiliateProduct>;
  collabs: Map<string, string>;
  samples: Map<string, TikTokSampleApplication>;
}

const time = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

function ProductLine({ product, id }: { product?: TikTokAffiliateProduct; id: string }) {
  return (
    <span className="flex items-center gap-2">
      {product?.imageUrl ? (
        <img src={product.imageUrl} alt="" className="h-10 w-10 rounded-md object-cover" />
      ) : (
        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
          <Package className="h-4 w-4 text-muted-foreground" />
        </span>
      )}
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{product?.title ?? `Product ${id}`}</span>
        {product?.priceMin && <span className="block text-xs opacity-75">${product.priceMin}</span>}
      </span>
    </span>
  );
}

function MessageBody({ m, lookups }: { m: TikTokMessage; lookups: Lookups }) {
  const c = m.content;
  switch (m.type) {
    case "PRODUCT_CARD":
      return <ProductLine id={c.productId ?? ""} product={lookups.products.get(c.productId ?? "")} />;
    case "TARGET_COLLABORATION_CARD":
      return (
        <span className="flex items-center gap-2">
          <Tag className="h-4 w-4 shrink-0" />
          <span>
            <span className="block text-sm font-medium">Collab invite</span>
            <span className="block text-xs opacity-75">{lookups.collabs.get(c.collaborationId ?? "") ?? "Open it on TikTok to see the offer"}</span>
          </span>
        </span>
      );
    case "FREE_SAMPLE_CARD": {
      const sample = lookups.samples.get(c.applicationId ?? "");
      return (
        <span className="flex items-center gap-2">
          <Gift className="h-4 w-4 shrink-0" />
          <span>
            <span className="block text-sm font-medium">Free sample request</span>
            <span className="block text-xs opacity-75">{sample?.product.title ?? "See it in the Samples tab"}</span>
          </span>
        </span>
      );
    }
    case "CRM_TEXT_WITH_PRODUCTS_CARD":
      return (
        <span className="block space-y-2">
          {c.title && <span className="block text-sm font-semibold">{c.title}</span>}
          {c.text && <span className="block whitespace-pre-wrap text-sm">{c.text}</span>}
          <span className="flex gap-1.5">
            {(c.productIds ?? []).map((id) => {
              const p = lookups.products.get(id);
              return p?.imageUrl ? (
                <img key={id} src={p.imageUrl} alt={p.title} title={p.title} className="h-10 w-10 rounded-md object-cover" />
              ) : (
                <span key={id} className="flex h-10 w-10 items-center justify-center rounded-md bg-muted" title={id}>
                  <Package className="h-4 w-4 text-muted-foreground" />
                </span>
              );
            })}
          </span>
        </span>
      );
    case "IMAGE":
    case "EMOTICONS":
    case "CRM_TEXT_WITH_IMAGE_CARD":
      return (
        <span className="block space-y-1">
          {c.imageUrl && <img src={c.imageUrl} alt="" className="max-h-48 rounded-md" />}
          {c.title && <span className="block text-sm font-semibold">{c.title}</span>}
          {c.text && <span className="block text-sm">{c.text}</span>}
        </span>
      );
    default:
      return <span className="whitespace-pre-wrap break-words text-sm">{c.text ?? ""}</span>;
  }
}

function Thread({
  conversation,
  lookups,
  creatorOpenId,
  sampleForCreator,
  onBack,
  onInvite,
}: {
  conversation: TikTokConversation;
  lookups: Lookups;
  creatorOpenId: string | null;
  sampleForCreator: TikTokSampleApplication | null;
  onBack: () => void;
  onInvite: (creators: TikTokCreatorPick[]) => void;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [attach, setAttach] = useState<Attach>(null);
  const [showcase, setShowcase] = useState<{ ids: string[]; title: string; text: string }>({ ids: [], title: "", text: "" });
  const [older, setOlder] = useState<TikTokMessage[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | null | undefined>(undefined);
  const bottom = useRef<HTMLDivElement>(null);

  const messages = useQuery({
    queryKey: ["tiktok-messages", conversation.id],
    queryFn: () => listTikTokMessages(conversation.id, conversation.creatorImId),
    refetchInterval: 10_000,
  });
  const collabs = useQuery({ queryKey: ["tiktok-collabs", "VALID", ""], queryFn: () => listTikTokCollaborations("VALID"), retry: false });

  useEffect(() => {
    setOlder([]);
    setOlderCursor(undefined);
    setAttach(null);
    setText("");
  }, [conversation.id]);

  const { mutate: markRead } = useMutation({
    mutationFn: () => markTikTokConversationRead(conversation.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tiktok-conversations"] });
      queryClient.invalidateQueries({ queryKey: ["tiktok-inbox-counts"] });
    },
  });
  useEffect(() => {
    if (conversation.unreadCount > 0) markRead();
  }, [conversation.id, conversation.unreadCount, markRead]);

  const latest = useMemo(() => messages.data?.messages ?? [], [messages.data]);
  const all = useMemo(() => {
    const seen = new Set<string>();
    return [...older, ...latest].filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
  }, [older, latest]);
  const nextOlder = olderCursor === undefined ? messages.data?.nextCursor ?? null : olderCursor;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [latest.length, conversation.id]);

  const loadOlder = async () => {
    if (!nextOlder) return;
    try {
      const page = await listTikTokMessages(conversation.id, conversation.creatorImId, nextOlder);
      setOlder((prev) => [...page.messages, ...prev]);
      setOlderCursor(page.nextCursor);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const send = useMutation({
    mutationFn: (message: TikTokOutgoingMessage) => sendTikTokMessage(conversation.id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tiktok-messages", conversation.id] });
      queryClient.invalidateQueries({ queryKey: ["tiktok-conversations"] });
      setAttach(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const sendText = () => {
    const body = text.trim();
    if (!body) return;
    send.mutate({ type: "TEXT", text: body }, { onSuccess: () => setText("") });
  };

  const status = useMutation({
    mutationFn: (action: "STARRED" | "UNSTARRED" | "ARCHIVED") => setTikTokConversationStatus(conversation.id, action),
    onSuccess: (_, action) => {
      toast.success(action === "ARCHIVED" ? "Conversation archived" : action === "STARRED" ? "Starred" : "Star removed");
      queryClient.invalidateQueries({ queryKey: ["tiktok-conversations"] });
      queryClient.invalidateQueries({ queryKey: ["tiktok-inbox-counts"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const products = [...lookups.products.values()];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 md:hidden" onClick={onBack} aria-label="Back to conversations">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <UserAvatar src={conversation.avatarUrl} name={conversation.username} className="h-9 w-9 text-xs" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">@{conversation.username}</p>
          {conversation.affiliate && <p className="truncate text-xs text-brand-700 dark:text-brand-400">Your affiliate {conversation.affiliate.name}</p>}
        </div>
        {creatorOpenId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="hidden sm:inline-flex"
            onClick={() => onInvite([{ openId: creatorOpenId, username: conversation.username, avatarUrl: conversation.avatarUrl }])}
          >
            <Send className="h-3.5 w-3.5" />
            Invite to collab
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Conversation actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => status.mutate("STARRED")}>Star</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => status.mutate("UNSTARRED")}>Remove star</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => status.mutate("ARCHIVED")}>Archive</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href={`https://www.tiktok.com/@${conversation.username}`} target="_blank" rel="noreferrer">
                Open TikTok profile
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-muted/20 px-4 py-4">
        {nextOlder && (
          <div className="text-center">
            <Button type="button" variant="ghost" size="sm" onClick={loadOlder}>
              Load earlier messages
            </Button>
          </div>
        )}
        {messages.isLoading && <SpinnerBlock className="py-12" />}
        {messages.error && <p className="py-12 text-center text-sm text-muted-foreground">{errorMessage(messages.error)}</p>}
        {!messages.isLoading && all.length === 0 && !messages.error && (
          <p className="py-12 text-center text-sm text-muted-foreground">No messages yet. Say hello, or share a product or an invite.</p>
        )}
        {all.map((m) =>
          m.from === "SYSTEM" ? (
            <p key={m.id} className="text-center text-[11px] text-muted-foreground">
              {m.content.text}
            </p>
          ) : (
            <div key={m.id} className={cn("flex", m.from === "SHOP" ? "justify-end" : "justify-start")}>
              <div
                className={cn(
                  "max-w-[80%] rounded-2xl px-3 py-2",
                  m.from === "SHOP" ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm border border-border bg-card text-foreground",
                )}
              >
                <MessageBody m={m} lookups={lookups} />
                <span className={cn("mt-1 block text-[10px]", m.from === "SHOP" ? "text-primary-foreground/70" : "text-muted-foreground")}>{time(m.createdAt)}</span>
              </div>
            </div>
          ),
        )}
        <div ref={bottom} />
      </div>

      {attach && (
        <div className="max-h-80 overflow-y-auto border-t border-border bg-card px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-foreground">
              {{ product: "Share a product", collab: "Share a collab invite", sample: "Share their sample request", showcase: "Product showcase card" }[attach]}
            </span>
            <button type="button" onClick={() => setAttach(null)} aria-label="Close" className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
          {attach === "product" &&
            (products.length === 0 ? (
              <p className="text-xs text-muted-foreground">No products in an open collaboration.</p>
            ) : (
              products.map((p) => (
                <button key={p.id} type="button" className="w-full rounded-md px-2 py-1.5 text-left hover:bg-muted" onClick={() => send.mutate({ type: "PRODUCT_CARD", productId: p.id })}>
                  <ProductLine product={p} id={p.id} />
                </button>
              ))
            ))}
          {attach === "collab" &&
            ((collabs.data?.items ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No active collab invites. Create one in the Invites tab.</p>
            ) : (
              collabs.data!.items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => send.mutate({ type: "TARGET_COLLABORATION_CARD", collaborationId: c.id })}
                >
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  {c.name}
                </button>
              ))
            ))}
          {attach === "sample" && sampleForCreator && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              onClick={() => send.mutate({ type: "FREE_SAMPLE_CARD", applicationId: sampleForCreator.applicationId })}
            >
              <Gift className="h-4 w-4 text-muted-foreground" />
              {sampleForCreator.product.title}
            </button>
          )}
          {attach === "showcase" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {products.map((p) => {
                  const on = showcase.ids.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        setShowcase((s) => ({ ...s, ids: on ? s.ids.filter((x) => x !== p.id) : s.ids.length >= 5 ? s.ids : [...s.ids, p.id] }))
                      }
                      className={cn("rounded-full border px-2.5 py-1 text-xs", on ? "border-brand-500 bg-brand-50 text-brand-800 dark:bg-brand-950 dark:text-brand-200" : "border-border")}
                    >
                      {p.title}
                    </button>
                  );
                })}
              </div>
              <Input placeholder="Title (optional)" maxLength={50} value={showcase.title} onChange={(e) => setShowcase((s) => ({ ...s, title: e.target.value }))} />
              <Textarea rows={2} placeholder="A short pitch" maxLength={CARD_TEXT_MAX} value={showcase.text} onChange={(e) => setShowcase((s) => ({ ...s, text: e.target.value }))} />
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  disabled={showcase.ids.length === 0 || !showcase.text.trim() || send.isPending}
                  onClick={() =>
                    send.mutate(
                      { type: "CRM_TEXT_WITH_PRODUCTS_CARD", productIds: showcase.ids, title: showcase.title.trim() || undefined, text: showcase.text.trim() },
                      { onSuccess: () => setShowcase({ ids: [], title: "", text: "" }) },
                    )
                  }
                >
                  Send card ({showcase.ids.length}/5)
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Right padding on phones keeps the send button clear of the floating support bubble. */}
      <div className="flex items-end gap-2 border-t border-border bg-card py-3 pl-3 pr-20 md:pr-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label="Attach a card">
              <Paperclip className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top">
            <DropdownMenuItem onSelect={() => setAttach("product")}>
              <Package className="h-4 w-4" />
              Product
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAttach("showcase")}>
              <LayoutGrid className="h-4 w-4" />
              Product showcase (up to 5)
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAttach("collab")}>
              <Tag className="h-4 w-4" />
              Collab invite
            </DropdownMenuItem>
            {sampleForCreator && (
              <DropdownMenuItem onSelect={() => setAttach("sample")}>
                <Gift className="h-4 w-4" />
                Their sample request
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Textarea
          rows={1}
          value={text}
          maxLength={TEXT_MAX}
          placeholder={`Message @${conversation.username}`}
          className="min-h-9 resize-none"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendText();
            }
          }}
        />
        <Button type="button" size="icon" className="h-9 w-9 shrink-0" disabled={!text.trim() || send.isPending} onClick={sendText} aria-label="Send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * The brand's TikTok affiliate inbox. Reads live from TikTok (polling; the
 * new-message webhook can push later), sends text and cards, and links back
 * to samples and invites.
 */
export function MessagesTab({
  pendingCreator,
  onPendingConsumed,
  onInvite,
}: {
  /** Open (or start) the conversation with this creator. */
  pendingCreator: TikTokCreatorPick | null;
  onPendingConsumed: () => void;
  onInvite: (creators: TikTokCreatorPick[]) => void;
}) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<TikTokConversationFilter>("ALL");
  const [active, setActive] = useState<TikTokConversation | null>(null);
  const [knownOpenIds, setKnownOpenIds] = useState<Record<string, string>>({});

  const conversations = useQuery({
    queryKey: ["tiktok-conversations", filter],
    queryFn: () => listTikTokConversations(filter),
    refetchInterval: 30_000,
    retry: false,
  });
  const counts = useQuery({ queryKey: ["tiktok-inbox-counts"], queryFn: getTikTokInboxCounts, refetchInterval: 30_000, retry: false });
  const products = useQuery({ queryKey: ["tiktok-affiliate-products"], queryFn: listTikTokAffiliateProducts, retry: false });
  const collabs = useQuery({ queryKey: ["tiktok-collabs", "VALID", ""], queryFn: () => listTikTokCollaborations("VALID"), retry: false });
  const pendingSamples = useQuery({ queryKey: ["tiktok-samples", "TO_REVIEW"], queryFn: () => listTikTokSamples("TO_REVIEW") });
  const progressSamples = useQuery({ queryKey: ["tiktok-samples", "IN_PROGRESS"], queryFn: () => listTikTokSamples("IN_PROGRESS") });

  const { mutate: openWith } = useMutation({
    mutationFn: (creator: TikTokCreatorPick) => openTikTokConversation(creator.openId),
    onSuccess: (conversation, creator) => {
      setActive(conversation);
      setKnownOpenIds((prev) => ({ ...prev, [conversation.username.toLowerCase()]: creator.openId }));
      queryClient.invalidateQueries({ queryKey: ["tiktok-conversations"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  useEffect(() => {
    if (pendingCreator) {
      openWith(pendingCreator);
      onPendingConsumed();
    }
  }, [pendingCreator, openWith, onPendingConsumed]);

  const samples = useMemo(() => [...(pendingSamples.data?.items ?? []), ...(progressSamples.data?.items ?? [])], [pendingSamples.data, progressSamples.data]);
  const lookups = useMemo<Lookups>(
    () => ({
      products: new Map((products.data ?? []).map((p) => [p.id, p])),
      collabs: new Map((collabs.data?.items ?? []).map((c) => [c.id, c.name])),
      samples: new Map(samples.map((s) => [s.applicationId, s])),
    }),
    [products.data, collabs.data, samples],
  );

  const block = conversations.error ? blockFromError(conversations.error) : null;
  if (block && block.kind !== "error") {
    return <TikTokBlockedState block={block} feature="creator messages" onRetry={() => conversations.refetch()} />;
  }

  const activeUser = active?.username.toLowerCase() ?? "";
  const sampleForCreator = samples.find((s) => s.creator.username.toLowerCase() === activeUser) ?? null;
  const creatorOpenId = knownOpenIds[activeUser] ?? sampleForCreator?.creator.openId ?? null;
  const list = conversations.data?.conversations ?? [];

  return (
    <div className="grid h-[calc(100vh-15rem)] min-h-[32rem] overflow-hidden rounded-lg border border-border bg-card md:grid-cols-[18rem_minmax(0,1fr)]">
      <div className={cn("flex min-h-0 flex-col border-r border-border", active && "hidden md:flex")}>
        <div className="flex flex-wrap gap-1 border-b border-border p-2">
          {FILTERS.map((f) => {
            const count = f.value === "ALL" ? null : counts.data?.[f.value];
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={cn(
                  "rounded-md px-2 py-1 text-xs font-medium",
                  filter === f.value ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {f.label}
                {count ? <span className="ml-1 tabular-nums text-brand-700 dark:text-brand-400">{count}</span> : null}
              </button>
            );
          })}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {conversations.isLoading ? (
            <SpinnerBlock className="py-12" />
          ) : conversations.error ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground">{errorMessage(conversations.error)}</p>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
              <Inbox className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{filter === "ALL" ? "No conversations yet. Message a creator from Social Search or a sample request." : "Nothing here"}</p>
            </div>
          ) : (
            list.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActive(c)}
                className={cn(
                  "flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left hover:bg-muted/50",
                  active?.id === c.id && "bg-muted",
                )}
              >
                <UserAvatar src={c.avatarUrl} name={c.username} className="h-9 w-9 shrink-0 text-xs" />
                <span className="min-w-0 flex-1">
                  <span className={cn("block truncate text-sm", c.unreadCount > 0 ? "font-semibold text-foreground" : "text-foreground")}>@{c.username}</span>
                  {c.affiliate && <span className="block truncate text-[11px] text-muted-foreground">Your affiliate</span>}
                </span>
                {c.unreadCount > 0 && (
                  <span className="min-w-5 rounded-full bg-brand-600 px-1.5 text-center text-[11px] font-medium tabular-nums text-white">{c.unreadCount}</span>
                )}
              </button>
            ))
          )}
        </div>
      </div>
      <div className={cn("min-h-0", !active && "hidden md:block")}>
        {active ? (
          <Thread
            conversation={active}
            lookups={lookups}
            creatorOpenId={creatorOpenId}
            sampleForCreator={sampleForCreator}
            onBack={() => setActive(null)}
            onInvite={onInvite}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <Inbox className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Pick a conversation</p>
          </div>
        )}
      </div>
    </div>
  );
}
